import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { inAppPath } from '@/lib/inAppPath';

export interface AppLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string | undefined;
  children: ReactNode;
}

/**
 * A link inside a message or a card.
 *
 * One that points back into this app is a ROUTE CHANGE - no second window, no
 * reload, and in the desktop client no trip to the system browser. Anything
 * else opens outside, as before. See `lib/inAppPath.ts` for what counts.
 *
 * Forwards its ref so it can be the child of a `Button asChild`.
 */
export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(
  { href, className, children, ...rest },
  ref,
) {
  const path = href === undefined || href === '' ? null : inAppPath(href);
  if (path !== null) {
    return (
      <Link ref={ref} to={path} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a ref={ref} href={href} target="_blank" rel="noreferrer noopener" className={className} {...rest}>
      {children}
    </a>
  );
});
